// frontend/src/contexts/UsageContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './FirebaseAuthContext';
import { apiService } from '../services/api';
import { logger } from '../services/logger/BrowserLogger';
import {
  UsageSummary,
  UsageStatus,
  PaywallState,
  PaywallTrigger,
  calculateTrialDaysRemaining,
} from '../types/paywall';
import { PaywallModal } from '../components/paywall';

/**
 * Usage Context Type
 * Provides usage state and actions throughout the app
 */
interface UsageContextType {
  // Core usage data
  usage: UsageSummary | null;
  isLoading: boolean;
  error: string | null;

  // Computed values
  creditsRemaining: number;
  lifetimeCreditsUsed: number;
  status: UsageStatus;
  trialEndsAt: Date | null;
  trialDaysRemaining: number | null;
  currentPeriodEndsAt: Date | null;
  totalCredits: number;

  // Helper flags
  isTrialActive: boolean;
  isTrialExpired: boolean;
  isProActive: boolean;
  hasCredits: boolean;
  shouldShowPaywall: boolean;

  // Actions
  refreshUsage: () => Promise<void>;
  checkFeatureAccess: (featureKey: string) => Promise<boolean>;
  decrementCreditsOptimistic: () => void;
  revertCreditsOptimistic: () => void;

  // Paywall modal control
  paywallState: PaywallState;
  openPaywall: (trigger: PaywallTrigger, featureKey?: string) => void;
  closePaywall: () => void;
}

/**
 * Create the context with undefined default
 */
const UsageContext = createContext<UsageContextType | undefined>(undefined);

/**
 * Hook to access usage context
 * Throws error if used outside provider
 */
export const useUsage = (): UsageContextType => {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

/**
 * Usage Provider Props
 */
interface UsageProviderProps {
  children: ReactNode;
}

/**
 * Usage Provider Component
 * Manages usage state and provides it to the app
 */
export const UsageProvider: React.FC<UsageProviderProps> = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();

  // Core state
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paywall modal state
  const [paywallState, setPaywallState] = useState<PaywallState>({
    isOpen: false,
    trigger: null,
    featureKey: null,
  });

  // Cache management
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const CACHE_DURATION_MS = 60 * 1000; // 1 minute

  /**
   * Fetch usage data from backend
   */
  const refreshUsage = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated || !currentUser) {
      setUsage(null);
      return;
    }

    // Check cache validity (skip if force refresh)
    const now = Date.now();
    if (!forceRefresh && usage && (now - lastFetchTime) < CACHE_DURATION_MS) {
      logger.debug('Using cached usage data', { cachedAt: new Date(lastFetchTime) });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logger.debug('Fetching usage data', { uid: currentUser.uid });
      const response = await apiService.getUserUsage();

      if (response.success && response.data) {
        setUsage(response.data);
        setLastFetchTime(now);
        logger.info('Usage data fetched', {
          credits: response.data.creditsRemaining,
          status: response.data.status,
        });
      } else {
        throw new Error('Failed to fetch usage data');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load usage information';
      setError(errorMessage);
      logger.error('Failed to fetch usage', err, { uid: currentUser.uid });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser, usage, lastFetchTime]);

  /**
   * Effect: Fetch usage on mount and auth changes
   */
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      refreshUsage(true); // Force refresh on auth change
    } else {
      setUsage(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser?.uid]);

  /**
   * Effect: Refresh usage when page becomes visible
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        refreshUsage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, refreshUsage]);

  /**
   * Computed values
   */
  const trialDaysRemaining = useMemo(() => {
    if (!usage?.trialEndsAt || usage.status !== 'trial-active') return null;
    return calculateTrialDaysRemaining(usage.trialEndsAt);
  }, [usage]);

  /**
   * Total credits from API response (fetched from Firestore billing config).
   * This ensures UI stays in sync with config updates without code deployment.
   */
  const totalCredits = useMemo(() => {
    // Use value from API, fallback to 200 (free trial credits) if usage not loaded
    return usage?.totalCredits ?? 200;
  }, [usage?.totalCredits]);

  // Helper flags
  const isTrialActive = usage?.status === 'trial-active';
  const isTrialExpired = usage?.status === 'trial-expired';
  const isProActive = usage?.status === 'pro-active';
  const hasCredits = (usage?.creditsRemaining ?? 0) > 0;

  /**
   * Paywall should be shown when:
   * 1. Trial has expired (regardless of credits), OR
   * 2. User has zero credits (regardless of plan type - trial or pro)
   *
   * CRITICAL: Pro users with creditsRemaining === 0 must be blocked.
   * This prevents unlimited usage after exhausting monthly credits.
   */
  const shouldShowPaywall = useMemo(() => {
    if (!isAuthenticated || !usage) return false;
    return isTrialExpired || !hasCredits;
  }, [isAuthenticated, usage, isTrialExpired, hasCredits]);

  /**
   * Check if user can access a feature
   * Returns true if user has access, false otherwise
   * Shows paywall modal if access denied
   */
  const checkFeatureAccess = useCallback(
    async (featureKey: string): Promise<boolean> => {
      if (!isAuthenticated) {
        logger.warn('Feature access check: User not authenticated', { featureKey });
        return false;
      }

      if (!usage) {
        logger.warn('Feature access check: Usage data not loaded', { featureKey });
        await refreshUsage(true);
        return false;
      }

      if (shouldShowPaywall) {
        logger.info('Feature access denied: Opening paywall', {
          featureKey,
          status: usage.status,
          credits: usage.creditsRemaining,
        });

        const trigger: PaywallTrigger = !hasCredits ? 'no-credits' : 'trial-expired';
        openPaywall(trigger, featureKey);
        return false;
      }

      logger.debug('Feature access granted', {
        featureKey,
        creditsRemaining: usage.creditsRemaining,
      });
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, usage, shouldShowPaywall, hasCredits, refreshUsage]
  );

  /**
   * Optimistically decrement credits for snappy UX
   * Should be called before API request
   */
  const decrementCreditsOptimistic = useCallback(() => {
    if (!usage) return;

    setUsage((prev) =>
      prev
        ? {
            ...prev,
            creditsRemaining: Math.max(0, prev.creditsRemaining - 1),
            lifetimeCreditsUsed: prev.lifetimeCreditsUsed + 1,
          }
        : null
    );

    logger.debug('Credits decremented optimistically', {
      newCredits: Math.max(0, (usage.creditsRemaining ?? 0) - 1),
    });
  }, [usage]);

  /**
   * Revert optimistic credit decrement on error
   * Should be called if API request fails
   */
  const revertCreditsOptimistic = useCallback(() => {
    if (!usage) return;

    setUsage((prev) =>
      prev
        ? {
            ...prev,
            creditsRemaining: Math.min(totalCredits, prev.creditsRemaining + 1),
            lifetimeCreditsUsed: Math.max(0, prev.lifetimeCreditsUsed - 1),
          }
        : null
    );

    logger.warn('Credits reverted due to error', {
      revertedTo: Math.min(totalCredits, (usage.creditsRemaining ?? 0) + 1),
    });
  }, [usage, totalCredits]);

  /**
   * Open paywall modal
   */
  const openPaywall = useCallback((trigger: PaywallTrigger, featureKey?: string) => {
    setPaywallState({
      isOpen: true,
      trigger,
      featureKey: featureKey || null,
    });

    logger.info('Paywall modal opened', { trigger, featureKey });
  }, []);

  /**
   * Close paywall modal
   */
  const closePaywall = useCallback(() => {
    setPaywallState({
      isOpen: false,
      trigger: null,
      featureKey: null,
    });

    logger.info('Paywall modal closed');
  }, []);

  /**
   * Context value
   */
  const value: UsageContextType = {
    // Core data
    usage,
    isLoading,
    error,

    // Computed values
    creditsRemaining: usage?.creditsRemaining ?? 0,
    lifetimeCreditsUsed: usage?.lifetimeCreditsUsed ?? 0,
    status: usage?.status ?? 'inactive',
    trialEndsAt: usage?.trialEndsAt ?? null,
    trialDaysRemaining,
    currentPeriodEndsAt: usage?.currentPeriodEndsAt ?? null,
    totalCredits,

    // Helper flags
    isTrialActive,
    isTrialExpired,
    isProActive,
    hasCredits,
    shouldShowPaywall,

    // Actions
    refreshUsage: () => refreshUsage(true),
    checkFeatureAccess,
    decrementCreditsOptimistic,
    revertCreditsOptimistic,

    // Paywall modal
    paywallState,
    openPaywall,
    closePaywall,
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
      <PaywallModal />
    </UsageContext.Provider>
  );
};
